import React, { useState, useEffect, useMemo } from 'react'
import {
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Paper,
  Divider,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material'
import { Close as CloseIcon, Settings as SettingsIcon } from '@mui/icons-material'
import { getObjectsWithFilter } from '../../api/apiClientCrud'

/** * Fonctions utilitaires déplacées hors des composants pour éviter la re-déclaration
 */
const not = (a, b) => a.filter((value) => b.indexOf(value) === -1)
const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1)

// --- COMPOSANT PRINCIPAL ---
const TransferListButton = ({ field, data, mode, onRowDataChange }) => {
  const [configOpen, setConfigOpen] = useState(false)
  const [attributes, setAttributes] = useState([])
  const [loading, setLoading] = useState(false)

  const { options = {} } = field
  const { objectAPI, object, filter, type: optionsType, data: optionsData } = options

  useEffect(() => {
    if (field.type !== 'transferList') return

    if (optionsType === 'api') {
      const fetchSelect = async () => {
        setLoading(true)
        try {
          const res = await getObjectsWithFilter(objectAPI ?? object, filter)
          setAttributes(res.data[object] ?? res.data.rows ?? [])
        } catch (error) {
          setAttributes([])
        } finally {
          setLoading(false)
        }
      }
      fetchSelect()
    } else if (optionsType === 'data') {
      setAttributes(optionsData || [])
    }
  }, [field.type, optionsType, objectAPI, object, JSON.stringify(filter)])

  const handleSave = (newIds) => {
    // On crée une copie propre de l'objet pour déclencher le rafraîchissement React
    const updatedData = {
      ...data,
      [field.name]: newIds
    }
    onRowDataChange?.(updatedData)
    setConfigOpen(false)
  }

  return (
    <>
      <Button
        startIcon={<SettingsIcon />}
        variant='outlined'
        size='small'
        disabled={loading || mode === 'view'}
        onClick={() => setConfigOpen(true)}
      >
        Configurer ({data[field.name]?.length || 0})
      </Button>

      <AttributeConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        option={data}
        allAttributes={attributes}
        onSave={handleSave}
      />
    </>
  )
}

// --- MODALE DE CONFIGURATION ---
const AttributeConfigDialog = ({ open, onClose, option, allAttributes, onSave }) => {
  // Filtrage par catégorie : on utilise useMemo pour la performance
  const filteredAttributes = useMemo(() => {
    return allAttributes.filter((attr) => attr.category === option?.target_category || attr.category === option?.type)
  }, [allAttributes, option?.target_category, option?.type])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Configuration : {option?.label || 'Option'}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
          Sélectionnez les éléments autorisés. Le prix sera multiplié par {option?.multiplier || 1}.
        </Typography>

        <TransferList
          allAttributes={filteredAttributes}
          initialSelectedIds={
            option?.allowedAttributes || option?.[Object.keys(option).find((k) => k.includes('Ids'))] || []
          }
          onSave={onSave}
        />
      </DialogContent>
    </Dialog>
  )
}

// --- LOGIQUE DE LA LISTE DE TRANSFERT ---
function TransferList({ allAttributes, initialSelectedIds, onSave }) {
  const [checked, setChecked] = useState([])
  const [left, setLeft] = useState([])
  const [right, setRight] = useState([])

  useEffect(() => {
    // Normalisation des IDs (Objets vers Nombres)
    const normalizedIds = initialSelectedIds.map((item) => (typeof item === 'object' ? item.id : item))

    setRight(allAttributes.filter((attr) => normalizedIds.includes(attr.id)))
    setLeft(allAttributes.filter((attr) => !normalizedIds.includes(attr.id)))
  }, [allAttributes, initialSelectedIds])

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]
    if (currentIndex === -1) newChecked.push(value)
    else newChecked.splice(currentIndex, 1)
    setChecked(newChecked)
  }

  const handleCheckedRight = () => {
    setRight((prev) => prev.concat(leftChecked))
    setLeft((prev) => not(prev, leftChecked))
    setChecked((prev) => not(prev, leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft((prev) => prev.concat(rightChecked))
    setRight((prev) => not(prev, rightChecked))
    setChecked((prev) => not(prev, rightChecked))
  }

  const renderList = (title, items) => (
    <Paper sx={{ width: 280, height: 400, display: 'flex', flexDirection: 'column' }} elevation={2}>
      <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Typography variant='subtitle1'>
          {title} ({items.length})
        </Typography>
      </Box>
      <Divider />
      <List dense sx={{ flex: 1, overflow: 'auto' }}>
        {items.map((item) => (
          <ListItem key={item.id} button onClick={handleToggle(item)}>
            <ListItemIcon>
              <Checkbox checked={checked.indexOf(item) !== -1} disableRipple />
            </ListItemIcon>
            <ListItemText primary={item.label} secondary={item.baseCost ? `+${item.baseCost}€` : null} />
          </ListItem>
        ))}
      </List>
    </Paper>
  )

  return (
    <Box>
      <Grid container spacing={2} justifyContent='center' alignItems='center'>
        <Grid item>{renderList('Disponibles', left)}</Grid>
        <Grid item>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant='outlined' size='small' onClick={handleCheckedRight} disabled={leftChecked.length === 0}>
              &gt;
            </Button>
            <Button variant='outlined' size='small' onClick={handleCheckedLeft} disabled={rightChecked.length === 0}>
              &lt;
            </Button>
          </Box>
        </Grid>
        <Grid item>{renderList('Sélectionnés', right)}</Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant='contained' onClick={() => onSave(right.map((r) => r.id))}>
          Enregistrer
        </Button>
      </Box>
    </Box>
  )
}

export default TransferListButton
